import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { cfetch } from '@confluence/network';
import { expVal } from '@confluence/feature-experiments';

import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload.ts';
import type { InsertStorageColumnTableRequestPayload } from '../__types__/InsertStorageColumnTableRequestPayload.ts';
import type { InsertMultipleStorageFragmentsRequestPayload } from '../__types__/InsertMultipleStorageFragmentsRequestPayload.ts';
import { PageMode } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import type { CreateIssueResponse } from '../__types__/apiUtils.ts';
import { constructTableWithWorkItems } from '../utils/tableUtils.tsx';
import type { ColumnToFieldMapping } from '../providers/BulkConfigureContextProvider.tsx';

const REFRESH_TIMEOUT = 3000;

export const constructJiraIssueUrl = (
	jiraSiteUrl: string | undefined,
	issueKey: string,
): string => {
	return `${jiraSiteUrl}/browse/${issueKey}`;
};

export const constructBreakContainerTag = (children: string) => {
	return `<div><br />${children}</div>`;
};

export const constructSmartlinkTag = (url: string) => {
	return `<a href="${url}" data-card-appearance="inline">${url}</a>`;
};

export const bulkConstructSmartlinkTags = (urls: string[]): string => {
	return urls.map((url) => constructSmartlinkTag(url)).join(' ');
};

export const constructHeading = (text: string): string => {
	return `<h2>${text}</h2>`;
};

export const getSummaryInsertionColumn = (
	columnToFieldMappings: ColumnToFieldMapping[] | null | undefined,
	isCreatedWithAi: boolean,
): number | undefined => {
	// When bulk creating with AI there is currently not support for mapping table columns to work item fields.
	// Therefore we always insert the summary into the first column.
	if (isCreatedWithAi) {
		return 0;
	}

	const summaryMapping = columnToFieldMappings?.find(
		(mapping) => mapping.field?.fieldId === 'summary',
	);
	return summaryMapping?.column?.index;
};

const getLastFetchTimeFromMetaTag = (): number | null => {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const metaTag = document.querySelector(`meta[name="confluence-request-time"]`);
	return metaTag ? Number(metaTag.getAttribute('content')) : null;
};

// Get last fetch time of current page.
// This used to be the request time, but this caused problems due to time drift between the server and database.
// So now try to use the content last modification date on load if that exists, then fallback onto the
// request time if it's not available.
const getLastFetchTime = (lastModified?: string | null) =>
	lastModified ? new Date(lastModified).getTime() : getLastFetchTimeFromMetaTag();

const constructBasePayload = (result: {
	searchText: {
		numMatches: number;
		index: number;
		selectedText: string;
	};
}) => {
	// This happens when the page is completely empty.
	if (!result || !result.searchText || result.searchText.numMatches === undefined) {
		return {
			selectedText: '',
			index: -1,
			numMatches: 0,
		};
	}

	const {
		searchText: { numMatches, index: matchIndex, selectedText },
	} = result;

	return {
		selectedText,
		index: matchIndex,
		numMatches,
	};
};

type ContentRef = HTMLElement | null | undefined;
type ConvertSelectionToLegacyFormat = (
	contentRef: ContentRef,
	range: Range | undefined,
	contentId: string,
) => {
	searchText: {
		numMatches: number;
		index: number;
		selectedText: string;
	};
};
export const constructInsertStorageFragmentRequestPayload = (
	contentId: string,
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat,
	range?: Range,
	contentRef?: HTMLElement | null,
	lastModified?: string | null,
) => {
	const result = convertSelectionToLegacyFormat(contentRef, range, contentId);
	return {
		...constructBasePayload(result),
		pageId: contentId,
		lastFetchTime: getLastFetchTime(lastModified),
		xmlModification: undefined,
	} as InsertStorageFragmentRequestPayload;
};

export const constructInsertMultipleStorageFragmentsRequestPayload = (
	contentId: string,
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat,
	ranges: Range[],
	contentRef?: HTMLElement | null,
	lastModified?: string | null,
) => {
	const result: InsertMultipleStorageFragmentsRequestPayload['fragments'] = [];
	let id = 0;
	for (const range of ranges) {
		result.push({
			...constructBasePayload(convertSelectionToLegacyFormat(contentRef, range, contentId)),
			xmlModification: undefined,
			rowIndex: String(id++),
		});
	}
	return {
		pageId: contentId,
		lastFetchTime: getLastFetchTime(lastModified),
		fragments: result,
	} as InsertMultipleStorageFragmentsRequestPayload;
};

export const constructInsertStorageColumnTableRequestPayload = (
	contentId: string,
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat,
	range?: Range,
	contentRef?: HTMLElement | null,
	lastModified?: string | null,
	tableColumnIndex?: number | null,
) => {
	const result = convertSelectionToLegacyFormat(contentRef, range, contentId);
	return {
		...constructBasePayload(result),
		pageId: contentId,
		lastFetchTime: getLastFetchTime(lastModified),
		tableColumnIndex: tableColumnIndex ?? 0,
		cellModifications: [],
	} as InsertStorageColumnTableRequestPayload;
};

/**
 * Updates the `lastFetchTime` property of the provided payload if the current page mode
 * is not `VIEW`. This is necessary because in modes other than `VIEW`, users can edit
 * the page. Therefore, the `lastFetchTime` must be updated to before the insertion occurs.
 * Failing to update `lastFetchTime` will result in insertion failures, as the backend will
 * perceive the page as modified since it was last fetched.
 */
export const updateLastFetchTimeIfNotViewMode = (
	payload:
		| InsertStorageFragmentRequestPayload
		| InsertStorageColumnTableRequestPayload
		| InsertMultipleStorageFragmentsRequestPayload,
	pageMode?: PageMode,
): void => {
	if (pageMode && pageMode !== PageMode.VIEW) {
		payload.lastFetchTime = Date.now();
	}
};

/**
 * Constructs an updated ADF node with a work item appended
 *
 * @param selectedAdf - ADF node of user's selection
 * @param workItemUrl - Work item URL to insert
 * @returns Updated table cell ADF node or null if conversion fails
 */
export const appendWorkItemToSelectedAdf = (
	selectedAdf: ADFEntity,
	workItemUrl: string,
): ADFEntity | null => {
	try {
		const selectedContent = selectedAdf.content ?? [];

		const updatedContent = [
			...selectedContent,
			{
				type: 'inlineCard',
				attrs: {
					url: workItemUrl,
				},
			},
		];

		return {
			type: selectedAdf.type,
			attrs: selectedAdf.attrs ?? {},
			content: updatedContent,
		};
	} catch {
		return null;
	}
};

export interface InsertSingleWorkItemParams {
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload;
	pageMode: PageMode;
	selectedAdf: ADFEntity | undefined;
	editorAPI: PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined;
	workItemUrl: string | undefined;
}

/**
 * Appends a single work item to the user's selection. When the user is in the editor,
 * the editor API is used. If the user is in the renderer or the editor API fails to insert,
 * the legacy storage fragment API is used.
 *
 * @param insertStorageFragmentRequestPayload - The payload for inserting storage fragment via the legacy API
 * @param pageMode - The current page mode (VIEW, EDIT, etc.)
 * @param selectedAdf - The selected ADF node, if any
 * @param editorAPI - The editor API instance for ADF replacement
 * @param workItemUrl - The work item URL to insert
 * @returns Promise<boolean | any> - Returns true if insertion via editor API succeeded,
 *   or the response from the legacy API call
 * @throws Error with message 'document-changed' if the document was modified during insertion
 */
export const insertSingleWorkItemToEndOfSelection = async ({
	insertStorageFragmentRequestPayload,
	pageMode,
	selectedAdf,
	editorAPI,
	workItemUrl,
}: InsertSingleWorkItemParams): Promise<any> => {
	if (pageMode !== PageMode.VIEW && selectedAdf && editorAPI && workItemUrl) {
		const updatedAdf = appendWorkItemToSelectedAdf(selectedAdf, workItemUrl);

		const result = await replaceWithAdfNode(editorAPI, updatedAdf);

		if (result.status === 'document-changed') {
			throw new Error('document-changed');
		}

		if (result.status === 'success') {
			// Return true to indicate that the insertion via the editor API was successful.
			// If not, we fall back to using the legacy API below.
			return true;
		}
	}

	return insertContentAtSelectionEnd(insertStorageFragmentRequestPayload);
};

export const insertContentAtSelectionEnd = async (
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload,
): Promise<any> => {
	try {
		const restUrl = '/wiki/rest/highlighting/1.0/insert-storage-fragment';

		const response = await cfetch(restUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(insertStorageFragmentRequestPayload),
		});

		if (!response.ok) {
			throw new Error('Insert storage fragment request failed');
		}

		return await response.json();
	} catch (error) {
		throw error;
	}
};

export const insertMultipleContentAtSelectionEnd = async (
	InsertMultipleStorageFragmentsRequestPayload: InsertMultipleStorageFragmentsRequestPayload,
): Promise<any> => {
	const restUrl = '/wiki/rest/highlighting/1.0/insert-storage-fragments';

	const response = await cfetch(restUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(InsertMultipleStorageFragmentsRequestPayload),
	});

	if (!response.ok) {
		throw new Error('Insert storage fragments request failed');
	}

	return response.json();
};

export const insertContentAtColumn = async (
	insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload,
): Promise<any> => {
	const restUrl = '/wiki/rest/highlighting/1.0/insert-storage-column-table';

	const response = await cfetch(restUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(insertStorageColumnTableRequestPayload),
	});
	if (!response.ok) {
		throw new Error('Insert storage column table request failed');
	}

	return response.json();
};

export const refreshPageWithTimeout = (): void => {
	setTimeout(() => {
		window.location.reload();
	}, REFRESH_TIMEOUT);
};

const findDeepestChild = (node: Node): Node => {
	let current = node;
	while (current.hasChildNodes()) {
		current = current.lastChild!;
	}
	return current;
};

/**
 * This function returns the last text node within the
 * action item Range as a Range object
 * @param originalRange The full Range of the action item
 * @returns
 */
export const getValidRangeForActionItem = (
	originalRange: Range | undefined | null,
): Range | undefined => {
	if (!originalRange) return undefined;
	const currentNode = findDeepestChild(originalRange.endContainer);
	return getValidRange(originalRange, currentNode);
};

/**
 * This function returns the last text node within the given range
 * @param originalRange
 * @returns
 */
export const getValidRange = (
	originalRange: Range | undefined | null,
	startNode?: Node,
): Range | undefined => {
	if (!originalRange) return undefined;

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const newRange = document.createRange();
	let lastTextNode: Text | null = null;
	let lastTextNodeStartOffset: number = 0;
	let lastTextNodeEndOffset: number = 0;

	const INVALID_NODE_SELECTORS = [
		// Status macro
		'span[data-node-type="status"]',
		// Inline Smart Card/Jira issue link
		'[data-inline-card="true"]',
		'.inlineCardView-content-wrap',
		'.smart-link-title-wrapper',
	].join(',');

	const isInvalidNode = (node: Node): boolean => {
		return node.nodeType === Node.ELEMENT_NODE && (node as Element).matches(INVALID_NODE_SELECTORS);
	};

	const hasInvalidAncestor = (node: Node, ancestorLimit: Node): boolean => {
		let currentNode: Node | null = node;
		while (currentNode && currentNode !== ancestorLimit) {
			if (isInvalidNode(currentNode)) {
				return true;
			}
			currentNode = currentNode.parentNode;
		}
		return false;
	};

	const previousNode = (node: Node, range: Range): Node | null => {
		if (node.previousSibling) {
			node = node.previousSibling;
			while (node.hasChildNodes() && !isInvalidNode(node)) {
				node = node.lastChild!;
			}
			return node;
		}
		return node.parentNode && node.parentNode !== range.commonAncestorContainer
			? node.parentNode
			: null;
	};

	let currentNode: Node | null = startNode ?? originalRange.endContainer;
	while (currentNode) {
		// If the current node is a text node and it is not empty, and it does not have an invalid ancestor, then it is a valid node.
		if (
			currentNode.nodeType === Node.TEXT_NODE &&
			currentNode.textContent &&
			currentNode.textContent.trim() !== '' &&
			!hasInvalidAncestor(currentNode, originalRange.commonAncestorContainer)
		) {
			lastTextNode = currentNode as Text;
			if (originalRange.endContainer === currentNode) {
				lastTextNodeEndOffset = originalRange.endOffset;
			} else {
				lastTextNodeEndOffset = currentNode.textContent.length;
			}
			if (originalRange.startContainer === currentNode) {
				lastTextNodeStartOffset = originalRange.startOffset;
			} else {
				lastTextNodeStartOffset = 0;
			}
			break; // Early termination as soon as we find the last valid text node.
		}
		// Terminate when we reach the start of the original range.
		if (currentNode === originalRange.startContainer) break;
		currentNode = previousNode(currentNode, originalRange);
	}

	if (lastTextNode) {
		newRange.setStart(lastTextNode, lastTextNodeStartOffset);
		newRange.setEnd(lastTextNode, lastTextNodeEndOffset);
		return newRange;
	}
	return originalRange;
};

export const getPageContentRange = (): Range | undefined => {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const contentElement = document.getElementById('main-content');
	if (contentElement) {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const range = document.createRange();
		range.selectNodeContents(contentElement);

		const endContainer = findDeepestChild(range.endContainer);
		let endOffset = 0;
		if (endContainer.nodeType === Node.TEXT_NODE) {
			endOffset = endContainer.textContent?.length || 0;
		} else if (endContainer.nodeType === Node.ELEMENT_NODE) {
			endOffset = (endContainer as Element).childNodes.length;
		}
		range.setEnd(endContainer, endOffset);

		return range;
	}
	return undefined;
};

/**
 * Replaces content using the provided ADF node via editorApi.replaceWithAdf
 *
 * @param editorApi - The editor API instance
 * @param adfNode - The ADF node to replace with
 * @returns Promise<{ status: 'success' | 'failed-to-replace' | 'document-changed' }>
 */
export const replaceWithAdfNode = async (
	editorApi: PublicPluginAPI<[SelectionExtensionPlugin]> | undefined,
	adfNode: ADFEntity | null,
): Promise<{ status: 'success' | 'failed-to-replace' | 'document-changed' }> => {
	if (!editorApi || !adfNode) {
		return { status: 'failed-to-replace' };
	}

	try {
		const result = await editorApi.selectionExtension?.actions.replaceWithAdf(adfNode);

		const status =
			result?.status === 'success' || result?.status === 'document-changed'
				? result?.status
				: 'failed-to-replace';

		return { status };
	} catch {
		return { status: 'failed-to-replace' };
	}
};

export interface InsertWorkItemsIntoTableParams {
	createdIssues: CreateIssueResponse[];
	currentJiraSiteUrl: string | undefined;
	pageMode: PageMode | undefined;
	selectedTableAdf: ADFEntity | undefined;
	editorAPI: PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined;
	payload: InsertStorageColumnTableRequestPayload;
	summaryInsertionColumn: number | undefined;
}

/**
 * Attempts to insert created work items into a selected table via the editor API
 *
 * @param createdIssues - The created issues to insert
 * @param currentJiraSiteUrl - The current base Jira URL
 * @param pageMode - The current page mode
 * @param selectedTableAdf - The selected table represented as ADF
 * @param editorAPI - The editor API to do ADF replacement
 * @param payload - The legacy column insertion payload
 * @param summaryInsertionColumn - The column index that is mapped to the summary field
 * @returns Promise<boolean> indicating whether the insertion succeeded
 */
export const insertWorkItemsIntoTable = async ({
	createdIssues,
	currentJiraSiteUrl,
	pageMode,
	selectedTableAdf,
	editorAPI,
	payload,
	summaryInsertionColumn,
}: InsertWorkItemsIntoTableParams): Promise<boolean> => {
	// Determine that we are in a non-view mode and whether we should use editorAPI for insertion
	const isEditorPathEnabled =
		expVal('confluence-bulk-work-create-editor', 'isEnabled', false) && pageMode !== PageMode.VIEW;

	if (isEditorPathEnabled && selectedTableAdf && editorAPI && summaryInsertionColumn != null) {
		// Construct map of row indices to work item URLs
		const workItemsByRow = new Map<number, string>();
		for (const issue of createdIssues) {
			if (issue.rowIndex != null) {
				workItemsByRow.set(
					Number(issue.rowIndex),
					constructJiraIssueUrl(currentJiraSiteUrl, issue.key),
				);
			}
		}

		const tableAdf = constructTableWithWorkItems(
			selectedTableAdf,
			workItemsByRow,
			summaryInsertionColumn,
		);
		if (!tableAdf) {
			return false;
		}
		const result = await replaceWithAdfNode(editorAPI, tableAdf);

		if (result.status === 'document-changed') {
			throw new Error('document-changed');
		}

		return result.status === 'success';
	}

	const response = await insertContentAtColumn(payload);
	return response;
};
